import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import useAutoScroll from '../../hooks/useAutoScroll';

const getImageUrl = (imgPath, w = 140) => {
  const fallback = "https://images.unsplash.com/photo-1516733968668-dbdce39c46ef?w=400&q=80";
  if (!imgPath) return fallback;
  let path = Array.isArray(imgPath) ? imgPath[0] : imgPath;
  if (!path) return fallback;
  if (path.startsWith('http')) return path;
  path = path.startsWith('/') ? path : `/${path}`;
  // Icons render at ~56px — request a tiny resized WebP, not the full image.
  return path.startsWith('/uploads') ? `${path}?w=${w}` : path;
};

const PremiumCategoryCard = ({ category }) => {
  const navigate = useNavigate();
  const count = Number(category.no_product ?? category.products ?? 0) || 0;

  const handleClick = () => {
    const categoryName = category.name || category.category_name;
    if (categoryName) navigate(`/products?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group shrink-0 w-[150px] sm:w-[190px] flex flex-col items-center text-center gap-2.5 sm:gap-3 p-3 sm:p-5 rounded-2xl border border-slate-200 bg-white hover:border-orange-300 hover:shadow-[0_12px_30px_rgba(249,115,22,0.12)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
        <img
          src={getImageUrl(category.image, 200)}
          alt={category.name}
          loading="lazy"
          decoding="async"
          width="80"
          height="80"
          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
        />
      </div>
      <div className="space-y-0.5">
        <p className="text-[11px] sm:text-sm font-bold text-slate-800 leading-tight line-clamp-2 group-hover:text-orange-600 transition-colors">
          {category.name}
        </p>
        <p className="text-[10px] sm:text-xs text-slate-400 font-medium">
          {count}+ products
        </p>
      </div>
    </button>
  );
};

export default function PremiumSection({ categories }) {
  const navigate = useNavigate();
  const { branding } = useSettings();
  const primaryColor = branding?.themeColor || '#f97316';
  const scrollRef = useAutoScroll({ deps: [categories?.length] });

  // Duplicate the list only when it actually overflows, so the seamless
  // infinite scroll has a second copy to loop through — but a short list that
  // fits on screen isn't shown twice.
  const [loop, setLoop] = React.useState(false);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setLoop(el.scrollWidth > el.clientWidth + 8);
  }, [categories]);

  if (!categories || categories.length === 0) return null;

  const items = loop ? [...categories, ...categories] : categories;

  return (
    <section className="w-full px-2 sm:px-4">
      <div className="rounded-3xl p-1 sm:p-2">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-7">
          <h2 className="text-lg sm:text-2xl font-black tracking-tight text-slate-900">
            Shop by Category
          </h2>
          <button
            type="button"
            onClick={() => navigate('/categories')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] sm:text-xs font-bold hover:opacity-80 transition-opacity whitespace-nowrap active:scale-95"
            style={{ color: primaryColor, backgroundColor: `${primaryColor}1a` }}
          >
            View all categories
            <ArrowRight size={14} />
          </button>
        </div>

        {/* Horizontal scrolling list (auto-scrolls; pauses on hover/touch) */}
        <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar py-3 -mx-1 px-2 scroll-smooth">
          {items.map((cat, i) => (
            <PremiumCategoryCard key={`${cat.id || cat.C_id || 'c'}-${i}`} category={cat} />
          ))}
        </div>
      </div>
    </section>
  );
}
